import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { PayoutItemsApiAdapter } from '../adapters';
import { PayoutItemsFilters } from '../models';

const PER_PAGE = 25;

export const usePayoutItems = () => {
  const httpClient = createHttpClient(apiClientWH);
  const payoutItemsAdapter = new PayoutItemsApiAdapter(httpClient);

  const [filters, setFilters] = useState<Omit<PayoutItemsFilters, 'page'>>({
    per_page: PER_PAGE,
    // No incluir sorting en el estado inicial - se establecerá cuando el usuario ordene
  });

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['cod-payout-items', filters],
    queryFn: ({ pageParam = 1 }) =>
      payoutItemsAdapter.getPayoutItems({ ...filters, page: pageParam as number }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta ?? {};
      // API devuelve camelCase (currentPage, lastPage) o snake_case (current_page, last_page)
      const currentPage = meta.current_page ?? meta.currentPage;
      const lastPageNum = meta.last_page ?? meta.lastPage;
      if (currentPage != null && lastPageNum != null && currentPage < lastPageNum) {
        return currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData, // 🎯 Mantener datos previos visibles durante refetch
  });

  const items = data?.pages.flatMap((page) => page?.data ?? []) ?? [];
  const meta = data?.pages?.[0]?.meta;
  const links = data?.pages?.[0]?.links;

  const updateFilters = (newFilters: Partial<PayoutItemsFilters>) => {
    setFilters((prev) => {
      const { page: _page, ...rest } = newFilters;
      return { ...prev, ...rest };
    });
  };

  const changePageSize = (per_page: number) => {
    setFilters((prev) => ({ ...prev, per_page }));
  };

  // Mapeo de campos de AG-Grid a campos de API
  // Solo amount y created_at son ordenables según los requisitos
  const FIELD_TO_API_SORT: Record<string, string> = {
    amount: 'amount',
    created_at: 'created_at',
  };

  const changeSorting = (column: string, direction: 'asc' | 'desc') => {
    setFilters((prev) => ({
      ...prev,
      'sorting[column]': column,
      'sorting[direction]': direction,
    }));
  };

  const handleSortingChange = (sorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    if (sorting) {
      const apiField = FIELD_TO_API_SORT[sorting.field] || sorting.field;
      changeSorting(apiField, sorting.direction);
    } else {
      // Limpiar sorting completamente cuando es null
      setFilters((prev) => {
        const { 'sorting[column]': _, 'sorting[direction]': __, ...rest } = prev;
        return { ...rest };
      });
    }
  };

  const resetFilters = () => {
    setFilters({
      per_page: PER_PAGE,
      // Limpiar también el sorting al resetear
    });
  };

  const hasFilters = useMemo(() => {
    return !!(
      filters.client_id ||
      filters.ref ||
      filters.cod_payout_id ||
      filters.without_payout ||
      filters.date_from ||
      filters.date_to
    );
  }, [filters]);

  return {
    items,
    meta,
    links,
    isLoading: isLoading || isPending,
    isFetching,
    error: error as Error | null,
    refetch,
    filters,
    updateFilters,
    changePageSize,
    changeSorting,
    handleSortingChange,
    resetFilters,
    hasFilters,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};

